import Project from '@/models/Project';
import ProjectGroup from '@/models/ProjectGroup';
import SharedFund from '@/models/SharedFund';
import ProjectGroupService from '@/services/ProjectGroupService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';
import { useSharedFundsStore } from './sharedFunds';

export type SharedFundSummary = {
  shared_fund: SharedFund;
  total_drawn: number;
  remaining: number;
  utilization_by_project: Record<number, number>;
};

export type ProjectSummary = {
  project: Project;
  total_drawn: number;
  utilization_by_fund: Record<number, number>;
};

export type ProjectGroupDetails = {
  project_group: ProjectGroup;
  projects_summary: ProjectSummary[];
  shared_funds_summary: SharedFundSummary[];
};

export const useProjectGroupsStore = defineStore('project_groups', {
  state: () => ({
    groupDetails: undefined as ProjectGroupDetails | undefined,
  }),

  getters: {
    models: () => useRepo(ProjectGroup),
  },

  actions: {
    async index(companyId: Id) {
      const [data, error] = await ProjectGroupService.index(companyId);

      if(error) {
        throw error;
      }

      if(data) {
        this.models.save(data);
      }

      return data;
    },

    async create(companyId: Id, request: ApiRequests['api.v1.companies.project-groups.store']) {
      const [data, error] = await ProjectGroupService.create(companyId, request);

      if(error) {
        throw error;
      }

      if(data) {
        this.models.save(data);
      }

      return data;
    },

    async show(id: Id) {
      const [data, error] = await ProjectGroupService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data.project_group);

      useSharedFundsStore().models.save(
        data.shared_funds_summary?.map((summary) => summary.shared_fund) ?? [],
      );

      this.groupDetails = data;

      return data;
    },

    async update(id: Id, request: ApiRequests['api.v1.project-groups.update']) {
      const [data, error] = await ProjectGroupService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async delete(id: Id) {
      const [data, error] = await ProjectGroupService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async report(id: Id, request: ApiRequests['api.v1.project-groups.report']) {
      const [data, error] = await ProjectGroupService.report(id, request);

      if(error) {
        throw error;
      }

      return data;
    },
  },
});
